import React, { useState } from 'react'
import Swal from 'sweetalert2'
import { motion } from 'framer-motion'
import {
  X, Plus, Pencil, Check, Layers, FileText, ExternalLink, History,
  CheckCircle2, XCircle, Loader2, Clock, Info, Inbox, Download, FileWarning,
} from 'lucide-react'
import { DocumentClass, DocumentClassSource, IndexingStatus } from '../../../api/apiTypes'
import {
  deactivateDocumentSource,
  registerBestExecutionSource,
  downloadSource,
} from '../../../api/functionApi'
import { labelMapping } from '../../../utils/documentLabels'
import SourceHistoryModal from './SourceHistoryModal'
import AddSourceModal, { NuovaFonte } from './AddSourceModal'
import NewVersionModal from './NewVersionModal'
import ClassValidationStrip from './ClassValidationStrip'

interface DocumentSourcesModalProps {
  openDocsModal: string | null
  setOpenDocsModal: React.Dispatch<React.SetStateAction<string | null>>
  documents: DocumentClass[]
  docSources: Record<string, DocumentClassSource[]>
  setDocSources: React.Dispatch<React.SetStateAction<Record<string, DocumentClassSource[]>>>
  fontiCounts: Record<string, number>
  setFontiCounts: React.Dispatch<React.SetStateAction<Record<string, number>>>
  reloadSources: () => Promise<void>
  /** Presente solo per le classi di una banca: /validate lavora per id. */
  bankId?: string | null
  /** Nome della banca: /ingest lavora per nome, non per id. */
  bankName?: string | null
}

/** Come si presenta lo stato di indicizzazione di una fonte. */
const STATO_INDICIZZAZIONE: Record<
  IndexingStatus,
  { label: string; Icon: React.ComponentType<{ className?: string }>; cls: string }
> = {
  pending: {
    label: 'Da indicizzare', Icon: Clock,
    cls: 'bg-gray-50 text-textSecondary border-borderDivider',
  },
  indexing: {
    label: 'In corso', Icon: Loader2,
    cls: 'bg-blue-50 text-blue-600 border-blue-200',
  },
  success: {
    label: 'Indicizzata', Icon: CheckCircle2,
    cls: 'bg-green-50 text-green-700 border-green-200',
  },
  error: {
    label: 'Errore', Icon: XCircle,
    cls: 'bg-red-50 text-red-700 border-red-200',
  },
}

const bottonePrimario =
  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-textPrimary text-white hover:bg-textSecondary transition-colors disabled:opacity-50'
const bottoneSecondario =
  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-buttonPrimaryBg text-buttonPrimaryText hover:bg-buttonPrimaryHover transition-colors disabled:opacity-50'

const DocumentSourcesModal: React.FC<DocumentSourcesModalProps> = ({
  openDocsModal,
  setOpenDocsModal,
  documents,
  docSources,
  setDocSources,
  fontiCounts,
  reloadSources,
  setFontiCounts,
  bankId,
  bankName,
}) => {
  const [loading, setLoading] = useState(false)
  const [showEditOptions, setShowEditOptions] = useState(false)
  const [showImportList, setShowImportList] = useState(false)
  const [selectedDocs, setSelectedDocs] = useState<Record<string, boolean>>({})
  const [canAddSources, setCanAddSources] = useState(false)
  const [addSourceOpen, setAddSourceOpen] = useState(false)
  const [newVersionOpen, setNewVersionOpen] = useState(false)
  /** Bumpato a ogni modifica delle fonti: fa ripetere /validate nella striscia. */
  const [validazioneToken, setValidazioneToken] = useState(0)

  if (!openDocsModal) return null

  const formattedOpenDocsModal =
    labelMapping[openDocsModal]?.label ||
    openDocsModal
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')

  const activeDocs = docSources[openDocsModal]?.filter(d => d.is_active) || []

  const handleCheckboxChange = (docId: string) => {
    setSelectedDocs(prev => ({ ...prev, [docId]: !prev[docId] }))
  }

  const handleImportCreate = async () => {
    if (!openDocsModal) return
    setLoading(true)
    try {
      const docsToDeactivate = activeDocs.filter(d => !selectedDocs[d.id])
      for (const doc of docsToDeactivate) {
        await deactivateDocumentSource(doc.id)
      }

      await reloadSources()
      setShowImportList(false)
      setShowEditOptions(true)
      setCanAddSources(true)
      setValidazioneToken(t => t + 1)
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: 'Nuova versione creata',
        showConfirmButton: false,
        timer: 2500,
        timerProgressBar: true,
      })
    } catch (err) {
      console.error('Errore handleImportCreate:', err)
      Swal.fire('Errore', 'Impossibile creare la nuova versione', 'error')
    } finally {
      setLoading(false)
    }
  }

  /** «Lista vuota»: disattiva tutte le fonti attive e apre l'aggiunta. */
  const handleListaVuota = async () => {
    setNewVersionOpen(false)
    setLoading(true)
    try {
      for (const doc of activeDocs) {
        await deactivateDocumentSource(doc.id)
      }
      await reloadSources()
      setShowImportList(false)
      setShowEditOptions(true)
      setCanAddSources(true)
      setValidazioneToken(t => t + 1)
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: 'Tutte le fonti sono state disattivate',
        showConfirmButton: false,
        timer: 2500,
        timerProgressBar: true,
      })
    } catch (err) {
      console.error(err)
      Swal.fire('Errore', 'Impossibile disattivare i documenti', 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleImportaPrecedente = () => {
    const initialSelection: Record<string, boolean> = {}
    activeDocs.forEach(d => {
      initialSelection[d.id] = true
    })
    setSelectedDocs(initialSelection)
    setNewVersionOpen(false)
    setShowImportList(true)
  }

  /**
   * Registra la fonte scelta nel modale. Rilancia l'errore: il pannello resta
   * aperto e mostra il `detail` del server, invece di chiudersi su un alert.
   */
  const handleAddSource = async (fonte: NuovaFonte) => {
    const currentDoc = documents.find(d => d.document_type === openDocsModal)
    if (!currentDoc) {
      throw new Error('Document class non trovata per ' + openDocsModal)
    }

    setLoading(true)
    try {
      // Una sola chiamata: registra i bytes nel layout per banca e crea il link.
      // Con `uri` include il download, quindi su un file remoto grosso puo'
      // richiedere ~30s; con `file` il tempo e' quello dell'upload.
      const linked = await registerBestExecutionSource({
        document_type: currentDoc.document_type,
        bank_id: currentDoc.bank_id ?? null,
        uri: fonte.uri ?? null,
        file: fonte.file ?? null,
        source_mimetype: fonte.source_mimetype,
      })

      setDocSources(prev => ({
        ...prev,
        [openDocsModal!]: [...(prev[openDocsModal!] || []), linked],
      }))

      setFontiCounts(prev => ({
        ...prev,
        [openDocsModal!]: (prev[openDocsModal!] || 0) + 1,
      }))

      // Niente /validate qui: registrare una fonte non cambia nulla che l'utente
      // debba vedere subito, e la classe resta comunque da indicizzare. Lo stato
      // si rilegge all'apertura del pannello o con il refresh della striscia.
      await reloadSources()
      setAddSourceOpen(false)

      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: 'Fonte aggiunta!',
        text: `${linked.source?.filename || linked.source?.uri || '–'}`,
        showConfirmButton: false,
        timer: 2500,
        timerProgressBar: true,
      })
    } finally {
      setLoading(false)
    }
  }

  const selezionate = activeDocs.filter(d => selectedDocs[d.id]).length

  return (
    <div className="fixed inset-0 bg-modalOverlay flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.98, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.15 }}
        className="bg-surface rounded-2xl shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col"
      >
        {/* Intestazione */}
        <div className="flex items-start justify-between gap-3 p-5 border-b border-borderDivider">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center shrink-0">
              <Layers className="w-4 h-4 text-textPrimary/50" />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-textPrimary truncate">
                {formattedOpenDocsModal}
              </h3>
              <p className="text-[11px] text-textMuted mt-0.5">
                {activeDocs.length} {activeDocs.length === 1 ? 'fonte attiva' : 'fonti attive'}
              </p>
              <div className="mt-2">
                <ClassValidationStrip
                  documentType={openDocsModal}
                  bankId={bankId}
                  bankName={bankName}
                  refreshToken={validazioneToken}
                />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {showImportList && (
              <>
                <button onClick={handleImportCreate} disabled={loading} className={bottonePrimario}>
                  <Check className="w-3.5 h-3.5" />
                  Conferma ({selezionate})
                </button>
                <button
                  onClick={() => setShowImportList(false)}
                  disabled={loading}
                  className={bottoneSecondario}
                >
                  Annulla
                </button>
              </>
            )}

            {!showImportList && !showEditOptions && (
              <button onClick={() => setShowEditOptions(true)} className={bottonePrimario}>
                <Pencil className="w-3.5 h-3.5" />
                Modifica
              </button>
            )}

            {showEditOptions && !showImportList && (
              canAddSources ? (
                <button onClick={() => setAddSourceOpen(true)} className={bottonePrimario}>
                  <Plus className="w-3.5 h-3.5" />
                  Aggiungi fonte
                </button>
              ) : (
                <>
                  <button
                    onClick={() => setNewVersionOpen(true)}
                    disabled={loading}
                    className={bottonePrimario}
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Crea nuova versione
                  </button>
                  <button onClick={() => setShowEditOptions(false)} className={bottoneSecondario}>
                    Annulla
                  </button>
                </>
              )
            )}

            <button
              onClick={() => {
                setOpenDocsModal(null)
                setCanAddSources(false)
              }}
              className="ml-1 text-textMuted hover:text-textPrimary transition-colors"
              title="Chiudi"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Corpo */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading && (
            <div className="flex items-center justify-center gap-2 text-xs text-textMuted py-6">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Caricamento…
            </div>
          )}

          {!loading && showImportList && (
            <div className="flex flex-col gap-3">
              <p className="text-xs text-textSecondary">
                Scegli quali fonti conservare nella nuova versione. Quelle non selezionate
                vengono disattivate.
              </p>
              <ul className="flex flex-col gap-2">
                {activeDocs.map((doc, idx) => {
                  const scelta = selectedDocs[doc.id] || false
                  return (
                    <motion.li
                      key={doc.id}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.03 }}
                    >
                      <button
                        type="button"
                        onClick={() => handleCheckboxChange(doc.id)}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${
                          scelta
                            ? 'border-green-200 bg-green-50/50'
                            : 'border-borderDivider bg-white hover:border-textPrimary/20'
                        }`}
                      >
                        <div
                          className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                            scelta ? 'bg-green-100' : 'bg-gray-50'
                          }`}
                        >
                          {scelta ? (
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                          ) : (
                            <FileText className="w-4 h-4 text-textPrimary/50" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold text-textPrimary truncate">
                            {doc.source?.filename || doc.source?.uri || '–'}
                          </div>
                          <div className="text-[11px] text-textMuted truncate">
                            {scelta ? 'Verrà conservata' : 'Verrà disattivata'}
                          </div>
                        </div>
                      </button>
                    </motion.li>
                  )
                })}
              </ul>
            </div>
          )}

          {!loading && !showImportList && activeDocs.length === 0 && (
            <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
              <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center">
                <Inbox className="w-5 h-5 text-textPrimary/40" />
              </div>
              <p className="text-xs font-medium text-textPrimary">Nessuna fonte inserita</p>
              <p className="text-[11px] text-textMuted max-w-xs">
                Usa «Modifica» per creare una versione della lista e aggiungere la prima fonte.
              </p>
            </div>
          )}

          {!loading && !showImportList && activeDocs.length > 0 && (
            <ul className="flex flex-col gap-2">
              {activeDocs.map((doc, idx) => (
                <DocRow
                  key={doc.id}
                  doc={doc}
                  formattedOpenDocsModal={formattedOpenDocsModal}
                  index={idx}
                />
              ))}
            </ul>
          )}
        </div>
      </motion.div>

      {addSourceOpen && (
        <AddSourceModal
          documentLabel={formattedOpenDocsModal}
          onClose={() => setAddSourceOpen(false)}
          onConferma={handleAddSource}
        />
      )}

      {newVersionOpen && (
        <NewVersionModal
          documentLabel={formattedOpenDocsModal}
          fontiAttive={activeDocs.length}
          onClose={() => setNewVersionOpen(false)}
          onListaVuota={handleListaVuota}
          onImporta={handleImportaPrecedente}
        />
      )}
    </div>
  )
}

/** Una fonte attiva: nome, stato, anno e periodo, più l'accesso allo storico. */
const DocRow: React.FC<{
  doc: DocumentClassSource
  formattedOpenDocsModal: string
  index: number
}> = ({ doc, formattedOpenDocsModal, index }) => {
  const [showHistory, setShowHistory] = useState(false)
  const [evidenza, setEvidenza] = useState<'fiscal' | 'periodo' | null>(null)
  const [scaricando, setScaricando] = useState(false)

  // La `uri` di una fonte Best Execution punta all'object store (gs://...):
  // apribile dal browser solo quando l'origine era davvero un URL remoto.
  const origineRemota = /^https?:\/\//i.test(doc.source?.uri ?? '')

  // Riga legacy: il catalogo ha la fonte ma non i bytes. Il download
  // risponderebbe "has no registered snapshot", ed e' la stessa condizione che
  // l'indicizzazione conta come `skipped_no_snapshot`. Si sa gia' da qui, quindi
  // il download non viene nemmeno offerto.
  const senzaSnapshot = !!doc.source && !doc.source.object_store_uri

  const scarica = async () => {
    if (!doc.source?.id) return
    setScaricando(true)
    try {
      const { blob, filename } = await downloadSource(doc.source.id)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      // Il BE espone Content-Disposition via CORS e ci mette l'estensione
      // giusta in base al mimetype: il fallback copre solo l'header assente.
      a.download = filename ?? doc.source.filename ?? 'documento'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      const messaggio = err instanceof Error ? err.message : ''
      // Il `detail` del BE qui e' tecnico (nomina SourceCatalog e la colonna):
      // va tradotto, perche' l'utente ha un'azione concreta da fare.
      const legacy = /no registered snapshot|object_store_uri/i.test(messaggio)
      Swal.fire(
        'Download non disponibile',
        legacy
          ? 'Di questa fonte non esiste una copia salvata: e\u2019 stata registrata prima che il server conservasse i contenuti. Ricarica il documento per renderlo scaricabile e indicizzabile.'
          : messaggio || 'Impossibile scaricare il file.',
        legacy ? 'info' : 'error',
      )
    } finally {
      setScaricando(false)
    }
  }

  const stato = doc.source?.indexing_status
    ? STATO_INDICIZZAZIONE[doc.source.indexing_status]
    : null

  const periodo =
    doc.source?.reporting_period_start && doc.source?.reporting_period_end
      ? `${new Date(doc.source.reporting_period_start).toLocaleDateString('it-IT')} – ${new Date(
          doc.source.reporting_period_end,
        ).toLocaleDateString('it-IT')}`
      : null

  return (
    <motion.li
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.03 }}
      className="p-3 rounded-xl border border-borderDivider bg-white hover:border-textPrimary/20 transition-all"
    >
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center shrink-0">
          <FileText className="w-4 h-4 text-textPrimary/50" />
        </div>

        <div className="flex-1 min-w-0">
          {/* Il nome file e' molto piu' leggibile dell'URL: "Report ANNO 2024.pdf"
              invece di "amfitalia.org/scarica-contenuto?id=Report%20ANNO%202024.pdf".
              Il nome scarica i bytes dall'API; l'origine remota, quando c'e',
              resta raggiungibile dall'icona accanto. */}
          <div className="flex items-center gap-1.5 max-w-full">
            {doc.source?.id && !senzaSnapshot ? (
              <button
                type="button"
                onClick={scarica}
                disabled={scaricando}
                title="Scarica il documento registrato"
                className="inline-flex items-center gap-1 text-xs font-semibold text-textPrimary hover:underline max-w-full disabled:opacity-50 disabled:no-underline"
              >
                <span className="truncate">
                  {doc.source.filename || doc.source.uri || '–'}
                </span>
                {scaricando ? (
                  <Loader2 className="w-3 h-3 shrink-0 animate-spin text-textMuted" />
                ) : (
                  <Download className="w-3 h-3 shrink-0 text-textMuted" />
                )}
              </button>
            ) : (
              <span className="text-xs font-semibold text-textPrimary truncate">
                {doc.source?.filename || doc.source?.uri || '–'}
              </span>
            )}

            {senzaSnapshot && (
              <span
                title="Fonte registrata prima dello snapshot dei contenuti: non essendoci una copia salvata non e' scaricabile, e l'indicizzazione la salta. Ricaricare il documento la sana."
                className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-medium bg-amber-50 text-amber-700 border-amber-200"
              >
                <FileWarning className="w-3 h-3" />
                Senza copia
              </span>
            )}

            {origineRemota && (
              <a
                href={doc.source!.uri}
                target="_blank"
                rel="noopener noreferrer"
                title={`Apri l'origine: ${doc.source!.uri}`}
                className="shrink-0 text-textMuted hover:text-textPrimary transition-colors"
              >
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-[11px] text-textMuted">
            <span>{formattedOpenDocsModal}</span>

            <span className="inline-flex items-center gap-1">
              Anno: {doc.source?.fiscal_year ?? '–'}
              {doc.source?.fiscal_year_evidence && (
                <button
                  type="button"
                  onClick={() => setEvidenza(e => (e === 'fiscal' ? null : 'fiscal'))}
                  className="text-textPrimary/40 hover:text-textPrimary transition-colors"
                  title="Da dove è stato dedotto"
                >
                  <Info className="w-3 h-3" />
                </button>
              )}
            </span>

            <span className="inline-flex items-center gap-1">
              Periodo: {periodo ?? '–'}
              {doc.source?.reporting_period_evidence && (
                <button
                  type="button"
                  onClick={() => setEvidenza(e => (e === 'periodo' ? null : 'periodo'))}
                  className="text-textPrimary/40 hover:text-textPrimary transition-colors"
                  title="Da dove è stato dedotto"
                >
                  <Info className="w-3 h-3" />
                </button>
              )}
            </span>
          </div>

          {evidenza && (
            <div className="mt-2 p-2 rounded-lg bg-gray-50 border border-borderDivider text-[11px] text-textSecondary break-words">
              {evidenza === 'fiscal'
                ? doc.source?.fiscal_year_evidence
                : doc.source?.reporting_period_evidence}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {stato && (
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-medium ${stato.cls}`}
            >
              <stato.Icon
                className={`w-3 h-3 ${doc.source?.indexing_status === 'indexing' ? 'animate-spin' : ''}`}
              />
              {stato.label}
            </span>
          )}

          {doc.source?.id && (
            <button
              onClick={() => setShowHistory(true)}
              title="Storico dei controlli di freschezza e delle indicizzazioni di questa fonte"
              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-borderDivider text-[11px] font-medium text-textSecondary hover:border-textPrimary/40 hover:text-textPrimary transition-all"
            >
              <History className="w-3 h-3" />
              Storico
            </button>
          )}
        </div>
      </div>

      {showHistory && doc.source?.id && (
        <SourceHistoryModal
          sourceId={doc.source.id}
          sourceLabel={doc.source.filename || doc.source.uri}
          onClose={() => setShowHistory(false)}
        />
      )}
    </motion.li>
  )
}

export default DocumentSourcesModal
